import React, { useEffect, useState } from 'react'
import { useI18n } from '../../i18n'
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  getUserModels,
  addUserModel,
  removeUserModel,
  getModels,
} from '../../api'
import { getApiErrorMessage } from '../../api'
import { useToast } from '../../contexts/ToastContext'
import ConfirmModal from '../../components/ConfirmModal'
import Modal from '../../components/Modal'
import { Select, Switch, Pagination, PageSection } from '../../components/ui'
import '../../styles/pages/settings.css'
import '../../styles/pages/page-common.css'

const ROLES = ['admin', 'agency', 'user', 'tester', 'tester_no_media', 'tester_plus']
const MODEL_ACCESS_ROLES = ['agency', 'user', 'tester_no_media']
const BILLING_PLANS = ['monthly_flat', 'per_bot_message', 'revenue_share', 'hybrid', 'custom']
const USERS_LIMIT = 20

function centsToAmount(value) {
  const cents = Number(value || 0)
  if (!Number.isFinite(cents) || cents === 0) return ''
  return (cents / 100).toFixed(2)
}

function amountToCents(value) {
  const normalized = String(value ?? '').trim().replace(',', '.')
  if (!normalized) return null
  const amount = Number(normalized)
  if (!Number.isFinite(amount) || amount < 0) return null
  return Math.round(amount * 100)
}

export default function SettingsUsers() {
  const { t } = useI18n()
  const { showToast } = useToast()
  const [users, setUsers] = useState([])
  const [usersTotal, setUsersTotal] = useState(0)
  const [usersPage, setUsersPage] = useState(0)
  const [models, setModels] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [modal, setModal] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  function loadUsers() {
    setLoading(true)
    const offset = usersPage * USERS_LIMIT
    getUsers({ limit: USERS_LIMIT, offset })
      .then((u) => {
        setUsers(Array.isArray(u?.data) ? u.data : [])
        setUsersTotal(u?.total ?? 0)
        setError(null)
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    getModels()
      .then((m) => setModels(Array.isArray(m) ? m : (Array.isArray(m?.data) ? m.data : [])))
      .catch(() => setModels([]))
  }, [])

  useEffect(() => {
    loadUsers()
  }, [usersPage])

  async function handleCreate(payload) {
    try {
      await createUser(payload)
      showToast(t('toast.user.created'), 'success')
      setModal(null)
      loadUsers()
    } catch (e) {
      throw new Error(getApiErrorMessage(e, t('users.createError')))
    }
  }

  async function handleUpdate(userId, payload) {
    try {
      const updated = await updateUser(userId, payload)
      showToast(t('toast.user.updated'), 'success')
      setUsers((prev) => prev.map((u) => (u.id === userId ? updated : u)))
      setModal(null)
    } catch (e) {
      throw new Error(getApiErrorMessage(e, t('users.updateError')))
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    try {
      await deleteUser(deleteTarget.id)
      showToast(t('toast.user.deleted'), 'success')
      setUsers((prev) => prev.filter((u) => u.id !== deleteTarget.id))
      setUsersTotal((prev) => Math.max(0, prev - 1))
      setDeleteTarget(null)
    } catch (e) {
      setError(getApiErrorMessage(e, t('users.deleteError')))
    }
  }

  if (loading) return <p className="settings-loading">{t('common.loading')}</p>
  if (error) return <p className="settings-error">{error}</p>

  return (
    <>
    <PageSection
      className="users-page"
      title={t('settings.nav.users')}
      hint={t('users.hint')}
      actions={
        <button type="button" className="btn btn-primary" onClick={() => setModal({ type: 'create' })}>
          {t('users.add')}
        </button>
      }
    >
      <div className="data-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('users.username')}</th>
              <th>{t('users.role')}</th>
              <th>{t('users.isActive')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.username}</td>
                <td>{t(`users.role.${u.role}`)}</td>
                <td>{u.is_active ? t('users.active') : t('users.inactive')}</td>
                <td>
                  {u.role !== 'admin' && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => setModal({ type: 'edit', user: u })}
                    >
                      {t('common.edit')}
                    </button>
                  )}
                  {MODEL_ACCESS_ROLES.includes(u.role) && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => setModal({ type: 'models', user: u })}
                    >
                      {t('users.models')}
                    </button>
                  )}
                  {u.role !== 'admin' && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => setDeleteTarget(u)}
                    >
                      {t('common.delete')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {usersTotal > 0 && (
          <Pagination
            page={usersPage}
            total={usersTotal}
            limit={USERS_LIMIT}
            onPageChange={setUsersPage}
            loading={loading}
            t={t}
          />
        )}
      </div>
    </PageSection>

      {modal?.type === 'create' && (
        <UserModal
          models={models}
          onClose={() => setModal(null)}
          onSave={handleCreate}
          t={t}
        />
      )}
      {modal?.type === 'edit' && (
        <UserModal
          user={modal.user}
          onClose={() => setModal(null)}
          onSave={(p) => handleUpdate(modal.user.id, p)}
          t={t}
        />
      )}
      {modal?.type === 'models' && (
        <UserModelsModal
          user={modal.user}
          models={models}
          onClose={() => setModal(null)}
          onAdd={addUserModel}
          onRemove={removeUserModel}
          getUserModels={getUserModels}
          showToast={showToast}
          t={t}
        />
      )}
      {deleteTarget && (
        <ConfirmModal
          open
          title={t('users.deleteConfirmTitle')}
          message={t('users.deleteConfirmMessage', { username: deleteTarget.username })}
          confirmLabel={t('common.delete')}
          danger
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </>
  )
}

function UserModal({ user, models = [], onClose, onSave, t }) {
  const [username, setUsername] = useState(user?.username ?? '')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState(user?.role ?? 'user')
  const [isActive, setIsActive] = useState(user?.is_active ?? true)
  const [agencyCanViewConditionalPrompts, setAgencyCanViewConditionalPrompts] = useState(
    Boolean(user?.agency_can_view_conditional_prompts),
  )
  const [agencyCanViewChatDiagnostics, setAgencyCanViewChatDiagnostics] = useState(
    Boolean(user?.agency_can_view_chat_diagnostics),
  )
  const [agencyEngineApiEnabled, setAgencyEngineApiEnabled] = useState(
    Boolean(user?.agency_engine_api_enabled),
  )
  const [agencyTrafficEnabled, setAgencyTrafficEnabled] = useState(
    Boolean(user?.agency_traffic_intelligence_enabled),
  )
  const [agencyBillingPlan, setAgencyBillingPlan] = useState(user?.agency_billing_plan ?? 'monthly_flat')
  const [agencyBillingCurrency, setAgencyBillingCurrency] = useState(user?.agency_billing_currency ?? 'USD')
  const [agencyBillingRate, setAgencyBillingRate] = useState(centsToAmount(user?.agency_billing_rate_cents))
  const [agencyBalance, setAgencyBalance] = useState(centsToAmount(user?.agency_balance_cents))
  const [agencyTopUp, setAgencyTopUp] = useState('')
  const [agencyLowBalanceThreshold, setAgencyLowBalanceThreshold] = useState(
    centsToAmount(user?.agency_low_balance_alert_threshold_cents),
  )
  const [selectedModelIds, setSelectedModelIds] = useState([])
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const isEdit = !!user
  const canEditUsername = isEdit && user?.role !== 'admin'
  const canAssignModelsOnCreate = !isEdit && MODEL_ACCESS_ROLES.includes(role)

  function toggleSelectedModel(modelId, checked) {
    setSelectedModelIds((prev) => {
      const current = new Set(prev)
      if (checked) current.add(modelId)
      else current.delete(modelId)
      return Array.from(current)
    })
  }

  function agencyPayload() {
    const balanceCents = amountToCents(agencyBalance) ?? 0
    const topUpCents = amountToCents(agencyTopUp) ?? 0
    return {
      agency_billing_plan: agencyBillingPlan,
      agency_billing_currency: (agencyBillingCurrency || 'USD').trim().toUpperCase(),
      agency_billing_rate_cents: amountToCents(agencyBillingRate),
      agency_balance_cents: balanceCents + topUpCents,
      agency_low_balance_alert_threshold_cents: amountToCents(agencyLowBalanceThreshold),
      agency_can_view_conditional_prompts: agencyCanViewConditionalPrompts,
      agency_can_view_chat_diagnostics: agencyCanViewChatDiagnostics,
      agency_engine_api_enabled: agencyEngineApiEnabled,
      agency_traffic_intelligence_enabled: agencyTrafficEnabled,
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setErr('')
    setSaving(true)
    try {
      if (isEdit) {
        const payload = { role, is_active: isActive }
        if (password) payload.password = password
        if (canEditUsername && username.trim()) payload.username = username.trim()
        if (role === 'agency') {
          Object.assign(payload, agencyPayload())
        }
        await onSave(payload)
      } else {
        if (!password || password.length < 6) {
          setErr(t('users.passwordMin'))
          setSaving(false)
          return
        }
        const payload = {
          username,
          password,
          role,
          is_active: isActive,
        }
        if (canAssignModelsOnCreate) payload.model_ids = selectedModelIds
        if (role === 'agency') {
          Object.assign(payload, agencyPayload())
        }
        await onSave(payload)
      }
    } catch (e) {
      setErr(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? `${t('users.editTitle')}: ${user?.username}` : t('users.createTitle')}>
      <form onSubmit={handleSubmit}>
        {err && <div className="form-error">{err}</div>}
        <div className="form-group">
          <label>{t('users.username')}</label>
          {canEditUsername ? (
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="off"
            />
          ) : isEdit ? (
            <div className="form-group-value">{user?.username}</div>
          ) : (
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="off"
            />
          )}
        </div>
        <div className="form-group">
          <label>{t('users.password')} {isEdit && `(${t('users.passwordLeaveEmpty')})`}</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={isEdit ? t('users.passwordPlaceholder') : ''}
            autoComplete="new-password"
            required={!isEdit}
          />
        </div>
        <div className="form-group">
          <label>{t('users.role')}</label>
          <Select
            value={role}
            onChange={setRole}
            options={ROLES.map((r) => ({ value: r, label: t(`users.role.${r}`) }))}
            placeholder={t('common.notSelected')}
            dropdownPosition="up"
          />
        </div>
        {role === 'agency' && (
          <div className="form-group user-agency-options">
            <h4>{t('users.agency.optionsTitle')}</h4>
            <Switch
              checked={agencyCanViewConditionalPrompts}
              onChange={setAgencyCanViewConditionalPrompts}
              label={t('users.agency.conditionalPromptsAccess')}
            />
            <p className="muted">{t('users.agency.conditionalPromptsHint')}</p>
            <Switch
              checked={agencyCanViewChatDiagnostics}
              onChange={setAgencyCanViewChatDiagnostics}
              label={t('users.agency.chatDiagnosticsAccess')}
            />
            <p className="muted">{t('users.agency.chatDiagnosticsHint')}</p>
            <Switch
              checked={agencyEngineApiEnabled}
              onChange={setAgencyEngineApiEnabled}
              label={t('users.agency.engineApiAccess')}
            />
            <p className="muted">{t('users.agency.engineApiHint')}</p>
            <Switch
              checked={agencyTrafficEnabled}
              onChange={setAgencyTrafficEnabled}
              label="Traffic Intelligence / OnlyTraffic"
            />
            <p className="muted">Enable the traffic workspace for this agency. Connect its Studio API key and map models in Traffic Intelligence after saving. Read-only: no traffic orders or budget changes.</p>
            <div className="user-agency-billing-grid">
              <label>
                <span>{t('users.agency.billingPlan')}</span>
                <Select
                  value={agencyBillingPlan}
                  onChange={setAgencyBillingPlan}
                  options={BILLING_PLANS.map((plan) => ({ value: plan, label: t(`users.billing.plan.${plan}`) }))}
                  dropdownPosition="up"
                />
              </label>
              <label>
                <span>{t('users.agency.billingCurrency')}</span>
                <input
                  type="text"
                  value={agencyBillingCurrency}
                  onChange={(event) => setAgencyBillingCurrency(event.target.value.toUpperCase())}
                  maxLength={8}
                  autoComplete="off"
                />
              </label>
              <label>
                <span>{t('users.agency.billingRate')}</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={agencyBillingRate}
                  onChange={(event) => setAgencyBillingRate(event.target.value)}
                  placeholder="0.00"
                />
              </label>
              <label>
                <span>{t('users.agency.balance')}</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={agencyBalance}
                  onChange={(event) => setAgencyBalance(event.target.value)}
                  placeholder="0.00"
                />
              </label>
              {isEdit && (
                <label>
                  <span>{t('users.agency.topUp')}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={agencyTopUp}
                    onChange={(event) => setAgencyTopUp(event.target.value)}
                    placeholder="0.00"
                  />
                </label>
              )}
              <label>
                <span>{t('users.agency.lowBalanceThreshold')}</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={agencyLowBalanceThreshold}
                  onChange={(event) => setAgencyLowBalanceThreshold(event.target.value)}
                  placeholder="0.00"
                />
              </label>
            </div>
            {isEdit && <p className="muted">{t('users.agency.topUpHint')}</p>}
          </div>
        )}
        {canAssignModelsOnCreate && (
          <div className="form-group user-model-access-picker">
            <label>{t('users.modelAccessTitle')}</label>
            <p className="muted">{t('users.modelAccessHint')}</p>
            <div className="user-model-access-list">
              {models.map((model) => (
                <label className="user-model-access-option" key={model.id}>
                  <input
                    type="checkbox"
                    checked={selectedModelIds.includes(model.id)}
                    onChange={(event) => toggleSelectedModel(model.id, event.target.checked)}
                  />
                  <span>{model.of_email || model.name || `#${model.id}`}</span>
                </label>
              ))}
              {models.length === 0 && <p className="muted">{t('users.noModels')}</p>}
            </div>
          </div>
        )}
        <div className="form-group">
          <Switch
            checked={isActive}
            onChange={setIsActive}
            label={t('users.isActive')}
          />
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function UserModelsModal({ user, models, onClose, onAdd, onRemove, getUserModels, showToast, t }) {
  const [userModelIds, setUserModelIds] = useState([])
  const [loading, setLoading] = useState(true)
  const [addingModelId, setAddingModelId] = useState('')
  const [removingId, setRemovingId] = useState(null)

  useEffect(() => {
    getUserModels(user.id)
      .then((r) => setUserModelIds(r.model_ids || []))
      .finally(() => setLoading(false))
  }, [user.id, getUserModels])

  async function handleAdd() {
    const id = addingModelId ? Number(addingModelId) : 0
    if (!id) return
    setAddingModelId('')
    try {
      await onAdd(user.id, id)
      showToast(t('toast.user.modelAdded'), 'success')
      setUserModelIds((prev) => [...prev, id])
    } catch (_) {}
  }

  async function handleRemove(modelId) {
    setRemovingId(modelId)
    try {
      await onRemove(user.id, modelId)
      showToast(t('toast.user.modelRemoved'), 'success')
      setUserModelIds((prev) => prev.filter((m) => m !== modelId))
    } finally {
      setRemovingId(null)
    }
  }

  const availableModels = models.filter((m) => !userModelIds.includes(m.id))
  const linkedModels = models.filter((m) => userModelIds.includes(m.id))

  return (
    <Modal open onClose={onClose} title={t('users.modelsTitle', { username: user.username })} modalClassName="modal--wide">
      {loading ? (
        <p>{t('common.loading')}</p>
      ) : (
        <>
          <div className="user-models-list">
            <h4>{t('users.modelsLinked')}</h4>
            {linkedModels.length === 0 ? (
              <p className="muted">{t('users.noModels')}</p>
            ) : (
              <ul>
                {linkedModels.map((m) => (
                  <li key={m.id}>
                    {m.of_email || `#${m.id}`}
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => handleRemove(m.id)}
                      disabled={removingId === m.id}
                    >
                      {t('common.delete')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="user-models-add">
            <h4>{t('users.modelsAdd')}</h4>
            <Select
              className="user-models-select"
              value={addingModelId ? String(addingModelId) : ''}
              onChange={(v) => setAddingModelId(v || '')}
              options={availableModels.map((m) => ({
                value: String(m.id),
                label: m.of_email || `#${m.id}`,
              }))}
              placeholder={t('common.notSelected')}
              dropdownPosition="up"
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAdd}
              disabled={!addingModelId}
            >
              {t('users.addModel')}
            </button>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}
